import { z } from 'zod';

import { opMeta } from '../op-meta.ts';
import { paramPath } from '../path.ts';
import { unifyColumnTypes } from '../schema/infer.ts';
import { booleanCol, type ColumnType, isNullable, nullableCol, unwrapNullable } from '../schema/types.ts';
import type { Value } from '../value.ts';
import { defineFold, defineUnary, isNullish } from './helpers.ts';

// Null.

export const isNull = z
  .object({
    name: z.literal('is-null'),
    params: z.tuple([z.unknown()]),
  })
  .describe('True iff the argument is null or missing (undefined — e.g. an absent struct field).')
  .register(opMeta, {
    terseGroup: 'logic',
    example: { expr: '(is-null null)', result: 'true' },
    ...defineUnary((v) => isNullish(v), undefined, booleanCol),
  });

export const isNotNull = z
  .object({
    name: z.literal('is-not-null'),
    params: z.tuple([z.unknown()]),
  })
  .describe('True iff the argument is not null and not missing (undefined).')
  .register(opMeta, {
    terseGroup: 'logic',
    example: { expr: '(is-not-null 5)', result: 'true' },
    ...defineUnary((v) => !isNullish(v), undefined, booleanCol),
  });

const coalesceBase = defineFold((vs) => {
  // `z.unknown()` widens to `{}` here — explicit cast restores `Value`.
  const found = vs.find((v) => !isNullish(v));
  return (found ?? null) as Value;
});
export const coalesce = z
  .object({
    name: z.literal('coalesce'),
    params: z.array(z.unknown()).min(1),
  })
  .describe('Return the first non-null, non-missing argument, or null if none.')
  .register(opMeta, {
    ...coalesceBase,
    example: { expr: '(coalesce null null 3)', result: '3' },
    expr: {
      ...coalesceBase.expr,
      // First non-null arg's type, with explicit nullable widening when
      // every arg is itself nullable. Strips `nullable<...>` wrappers
      // before unifying so two args of `nullable<number>` unify cleanly
      // to `number` (then re-wrap if all-nullable).
      inferType: (call, infer, path): ColumnType => {
        const params = call.params ?? [];
        const argTypes = params.map((p, i) => infer(p, paramPath(path, i)));
        const unified = unifyColumnTypes(argTypes.map(unwrapNullable));
        return argTypes.length > 0 && argTypes.every(isNullable) ? nullableCol(unified) : unified;
      },
    },
  });
